import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Copy, Check, Send, Linkedin, UserPlus, ExternalLink, Sparkles, Clock, Pencil } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { executeConnectionTask, executeMessageTask } from '../../../redux/DailyTasksSlice/dailyTaskSlice';
import api from '../../../api/axios';
import { toast } from 'react-toastify';

const EmailGenerationModal = ({ isOpen, onClose, contact, jobData, companyData, outreachMode = 'email', onSuccess, taskId, inviteStatus, emailType = 'first', previousEmail = null, step }) => {
    const MAX_NOTE_LENGTH = 300;
    const [generatingEmail, setGeneratingEmail] = useState(false);
    const [emailContent, setEmailContent] = useState(null);
    const [copied, setCopied] = useState(false);
    const [promptText, setPromptText] = useState('');
    const [editableSubject, setEditableSubject] = useState('');
    const [editableBody, setEditableBody] = useState('');
    const [isSendingToLi, setIsSendingToLi] = useState(false);
    const [allowInMail, setAllowInMail] = useState(false);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const isGoogleConnected = useSelector((state) => state.auth.isGoogleConnected);
    const { isLinkedInConnected } = useSelector((state) => state.liauth);

    // Sending email goes out AS the user via the Gmail API, which needs their
    // Google account connected. Send them through the OAuth flow if it isn't.
    const handleConnectGoogle = () => {
        // Open Profile in a NEW tab and let it auto-start the Google connect, so the
        // user keeps this email draft in the current tab.
        window.open('/profile?connectGoogle=1', '_blank', 'noopener');
    };
    const isLinkedInMode = outreachMode === 'message' || outreachMode === 'note';
    const isNoteMode = outreachMode === 'note';
    
    const clampLinkedInNote = (value) => {
        const normalized = (value ?? '').toString().replace(/\r\n/g, '\n');
        return normalized.length > MAX_NOTE_LENGTH ? normalized.slice(0, MAX_NOTE_LENGTH) : normalized;
    };

    useEffect(() => {
        if (isOpen && contact) {
            generateEmail();
        }
    }, [isOpen, contact]);

    useEffect(() => {
        if (isOpen) {
            // For LinkedIn message mode, default to sending directly: connected → normal
            // message, not-connected → InMail. This skips the "connect first" wall.
            // (Note mode is a connection-request note, so it stays false.)
            setAllowInMail(outreachMode === 'message');
        }
    }, [isOpen, inviteStatus, outreachMode]);

    const generateEmail = async (userPrompt = '') => {
        setGeneratingEmail(true);
        setEmailContent(null);

        const defaultPrompt = isNoteMode
            ? 'Write an extremely concise LinkedIn connection request note. 1–2 short sentences, friendly and human. No "Dear", no formal email tone. Mention 1 specific reason to connect. Target <= 220 characters (hard max 300).'
            : isLinkedInMode
                ? 'Write a short, personalized LinkedIn message (4-6 lines) for this recruiter. Keep it concise and human, referencing role fit and one clear ask.'
                : '';

        const buildEffectivePrompt = () => {
            const raw = (userPrompt || defaultPrompt || '').trim();
            if (!raw) return undefined;
            if (!isNoteMode) return raw;

            // LinkedIn connection notes have a hard 300-character limit.
            // Also bias toward ultra-concise output so it doesn't look like an email.
            return `${raw}\n\nConstraints:\n- Must be 1–2 short sentences.\n- No "Dear", no formal email tone.\n- Target <= 220 characters. Hard max: 300 characters (including spaces).`;
        };
        
        try {
            // Call backend to generate email using DeepSeek
            const response = await api.post('/api/deepseek/generate-email', {
                userName: user?.name || 'Candidate',
                userEmail: user?.email || '',
                userProfile: {
                    skills: user?.skills || [],
                    experience: user?.experience || [],
                    education: user?.education || [],
                    summary: user?.summary || user?.bio || '',
                    // Without these the model invents a phone number and a
                    // LinkedIn URL for the sign-off. Passed so it uses the real
                    // ones, or omits the line entirely.
                    phone: user?.contact || user?.phone || null,
                    linkedinUrl: user?.linkedinUrl || null
                },
                // Lets the backend store this draft against the contact, so the
                // Daily Digest reuses it instead of writing a different message
                // to the same person tomorrow.
                contactId: contact?.contactid || contact?.contactId || contact?._id || null,
                contactName: contact?.name || 'Recruiter',
                contactTitle: contact?.title || 'Recruiter',
                contactEmail: contact?.email || '',
                companyName: companyData?.name || contact?.organization_name || 'the company',
                jobTitle: jobData?.jobPosition || companyData?.jobPosition || 'Open Position',
                jobDescription: jobData?.jobDescription || jobData?.description || '',
                recruiterBackground: contact?.analysis_summary || contact?.strategic_rationale || contact?.reason || '',
                outreachMode,
                emailType,
                ...(emailType === 'followup' && previousEmail ? { previousEmail: { subject: previousEmail.subject || '', body: previousEmail.body || '' } } : {}),
                userPrompt: buildEffectivePrompt(),
                ...(userPrompt
                    ? {
                        currentEmail: {
                            subject: editableSubject || '',
                            body: editableBody || ''
                        }
                    }
                    : {}),
            });

            if (response.data && response.data.email) {
                setEmailContent(response.data.email);
                setEditableSubject(response.data.email.subject || '');
                const nextBody = response.data.email.body || '';
                setEditableBody(isNoteMode ? clampLinkedInNote(nextBody) : nextBody);
            } else {
                throw new Error('Invalid response format');
            }
        } catch (error) {
            console.error('Error generating email:', error);
            
            // Fallback email template
            const fallbackEmail = isNoteMode
                ? {
                    subject: 'Connection Request',
                    body: `Hi ${contact?.name || 'there'}, I'd love to connect and learn more about your work at ${companyData?.name || 'the company'}. Best, ${user?.name || 'Candidate'}`.slice(0, MAX_NOTE_LENGTH)
                }
                : isLinkedInMode
                    ? {
                        subject: `LinkedIn message for ${contact?.name || 'recruiter'}`,
                        body: `Hi ${contact?.name || 'there'},

I came across the ${jobData?.jobPosition || companyData?.jobPosition || 'role'} at ${companyData?.name || contact?.organization_name || 'your company'} and would love to connect.

My background aligns well with this opportunity, and I would appreciate a quick conversation about fit and next steps.

Thanks,
${user?.name || 'Candidate'}`
                    }
                    : {
                        subject: `Regarding ${jobData?.jobPosition || companyData?.jobPosition || 'opportunities'} at ${companyData?.name || contact?.organization_name}`,
                        body: `Dear ${contact?.name},

I hope this email finds you well. I came across the ${jobData?.jobPosition || companyData?.jobPosition || 'position'} at ${companyData?.name || contact?.organization_name} and I'm very interested in learning more about this opportunity.

With my background and experience, I believe I would be a strong fit for this role. I would appreciate the opportunity to discuss how my skills align with your team's needs.

Would you be available for a brief conversation?

Best regards,
${user?.name || 'Candidate'}`
                    };
            
            setEmailContent(fallbackEmail);
            setEditableSubject(fallbackEmail.subject);
            setEditableBody(isNoteMode ? clampLinkedInNote(fallbackEmail.body) : fallbackEmail.body);
        } finally {
            setGeneratingEmail(false);
        }
    };

    const copyToClipboard = () => {
        const fullEmail = `Subject: ${editableSubject}\n\n${editableBody}`;
        navigator.clipboard.writeText(isLinkedInMode ? editableBody : fullEmail);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };


    // Send the AI-drafted email directly from the user's connected Google account.
    const handleSendViaGmail = async () => {
        if (!contact?.email) { toast.error('No email address for this contact.'); return; }
        if (!editableSubject.trim() || !editableBody.trim()) { toast.error('Subject and body are required.'); return; }
        setIsSendingEmail(true);
        try {
            await api.post('/api/gmail/sendemail', {
                to: contact.email,
                subject: editableSubject,
                body: editableBody,
                contactName: contact?.name || '',
                organizationName: companyData?.name || contact?.organization_name || '',
                industry: contact?.industry || companyData?.industry || 'your industry',
                contactId: contact?.contactid || contact?._id,
                emailType, // 'first' | 'followup' — drives threading + persistence
                step,
            });
            toast.success(`Email sent to ${contact.name}!`);
            if (onSuccess) onSuccess({ sent: true, subject: editableSubject, body: editableBody });
            onClose();
        } catch (err) {
            const msg = err?.response?.data?.msg || 'Failed to send email.';
            if (/google account not connected|reconnect your google/i.test(msg)) {
                toast.error('Connect your Google account in Profile to send email from the platform.');
            } else {
                toast.error(msg);
            }
        } finally {
            setIsSendingEmail(false);
        }
    };

    const handleSendToLinkedIn = async () => {
        if (!isLinkedInConnected?.accountId || !contact || !taskId) return;
        const noteBody = isNoteMode ? editableBody.slice(0, MAX_NOTE_LENGTH) : editableBody;
        
        setIsSendingToLi(true);
        try {
            if (isNoteMode) {
                await dispatch(executeConnectionTask({
                    taskId,
                    text: noteBody,
                    providerId: contact?.providerId,
                    contactId: contact?.contactid || contact?._id,
                    profileUrl: contact?.linkedin_url,
                    accountId: isLinkedInConnected.accountId
                })).unwrap();
            } else {
                // message mode
                await dispatch(executeMessageTask({
                    taskId,
                    text: editableBody,
                    contactId: contact?.contactid || contact?._id,
                    attendeeId: contact?.providerId,
                    accountId: isLinkedInConnected.accountId,
                    userId: user?._id,
                    profileUrl: contact?.linkedin_url || contact?.profile_url,
                    isInMail: inviteStatus !== 'accepted' && allowInMail
                })).unwrap();
            }
            if (onSuccess) onSuccess({ automated: true });
            onClose();
        } catch (err) {
            console.error("Failed to execute automated task via Unipile:", err);
            const errorMsg = err?.message || '';
            if (errorMsg.toLowerCase().includes('not a 1st-degree connection')) {
                toast.warning('This recipient is not a 1st-degree connection yet. Use InMail or connect first.');
            }
        } finally {
            setIsSendingToLi(false);
        }
    };

    if (!isOpen) return null;

    const handleOverlayClick = () => onClose && onClose();

    const needsConnectionForMessage = outreachMode === 'message' && inviteStatus !== 'accepted' && !allowInMail;
    const currentLength = editableBody.length;

    // ── design-driven derived values ──
    const modalTitle = isNoteMode ? 'Generated LinkedIn Note' : isLinkedInMode ? 'Generated Message' : 'Generated Email';
    const subjectLabel = isLinkedInMode ? (isNoteMode ? 'Note heading' : 'Message heading') : 'Subject line';
    const bodyLabel = isNoteMode ? 'Note body' : isLinkedInMode ? 'Message body' : 'Email body';
    const initials = (n) => String(n || '?').trim().split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase();
    const tierLabel = contact?._tierLabel || (/(recruit|talent acquisition)/i.test(contact?.title || '') ? 'Tier 1' : 'Contact');
    const wordCount = editableBody.trim() ? editableBody.trim().split(/\s+/).length : 0;
    const readSec = Math.max(10, Math.round((wordCount / 200) * 60));
    const refineChips = ['Make it shorter', 'More casual', 'Add my portfolio', 'Stronger CTA'];

    return createPortal(
        <div
            className="fixed inset-0 grid place-items-center p-4"
            style={{ zIndex: 9000, background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(8px)' }}
            onClick={handleOverlayClick}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white flex flex-col w-full"
                style={{ width: 680, maxWidth: '100%', borderRadius: 20, boxShadow: '0 30px 80px rgba(15,23,42,.5)', maxHeight: '92vh', overflow: 'hidden' }}
            >
                {/* Header */}
                <div className="flex items-center gap-3.5 px-6 py-5 border-b border-[#EFF1F5]">
                    <div className="grid place-items-center text-white flex-shrink-0" style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #3F57E0, #6478F0)', boxShadow: '0 6px 14px rgba(63,87,224,.3)' }}>
                        <Sparkles style={{ width: 20, height: 20 }} />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                        <div className="flex items-center gap-2">
                            <span style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.01em', color: '#0F172A' }}>{modalTitle}</span>
                            <span className="inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: '#EEF1FE', color: '#3F57E0' }}>✨ AI draft</span>
                        </div>
                        <div className="text-[12px] text-[#64748B] mt-0.5">Personalized for {contact?.name || 'contact'} · ready to send</div>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg border border-[#E6E8EE] text-[#94A3B8] hover:border-[#D6DAE3] hover:text-[#475569] transition-colors flex-shrink-0">
                        <X size={14} />
                    </button>
                </div>

                {generatingEmail ? (
                    <div className="flex flex-col items-center justify-center px-6 py-20">
                        <div className="grid place-items-center text-white mb-4 animate-pulse" style={{ width: 52, height: 52, borderRadius: 14, background: 'linear-gradient(135deg, #3F57E0, #6478F0)' }}>
                            <Sparkles size={24} />
                        </div>
                        <p className="text-[15px] font-bold text-[#0F172A]">{isLinkedInMode ? 'Crafting your message…' : 'Crafting your personalized email…'}</p>
                        <p className="text-[12.5px] text-[#64748B] mt-1">Analyzing your profile and the role</p>
                        <div className="w-48 bg-[#EEF1FE] rounded-full h-1.5 mt-4 overflow-hidden">
                            <div className="bg-[#3F57E0] h-1.5 rounded-full animate-pulse" style={{ width: '70%' }} />
                        </div>
                    </div>
                ) : emailContent ? (
                    <>
                        {/* Scroll body */}
                        <div className="px-6 pt-5 pb-2 overflow-y-auto">
                            {/* Recipient card */}
                            <div className="flex items-center gap-3.5 mb-4" style={{ padding: '14px 16px', borderRadius: 14, background: 'linear-gradient(135deg, #EEF1FE 0%, #F8FAFF 80%)', border: '1px solid #E0E5FC' }}>
                                <div className="grid place-items-center text-white flex-shrink-0" style={{ width: 44, height: 44, borderRadius: 9999, background: 'linear-gradient(135deg, #9333EA, #6478F0)', fontSize: 15, fontWeight: 700 }}>{initials(contact?.name)}</div>
                                <div className="flex-1 min-w-0 text-left">
                                    <div className="text-[10.5px] font-semibold uppercase tracking-wider text-[#3F57E0]">Recipient</div>
                                    <div className="text-[14.5px] font-bold text-[#0F172A] mt-0.5 truncate">{contact?.name}{contact?.title && <span className="font-medium text-[#64748B]"> · {contact.title}</span>}</div>
                                    {isLinkedInMode && !isNoteMode && (
                                        <div className="mt-1">
                                            {inviteStatus === 'accepted' ? (
                                                <span className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-emerald-700">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Connected · sends as a direct message
                                                </span>
                                            ) : inviteStatus === 'pending' ? (
                                                <span className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-amber-600">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Invite pending · will send as InMail
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-slate-500">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400" /> Not connected · will send as InMail
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {!isLinkedInMode && (
                                        contact?.email ? (
                                            <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                                                <span className="text-[12.5px] font-semibold text-[#3F57E0] truncate">{contact.email}</span>
                                                <button
                                                    onClick={() => { navigator.clipboard.writeText(contact.email); toast.success('Email address copied'); }}
                                                    className="text-[#94A3B8] hover:text-[#3F57E0] flex-shrink-0 transition-colors"
                                                    title="Copy email address"
                                                >
                                                    <Copy size={12} />
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="text-[12.5px] font-semibold text-[#94A3B8] mt-0.5">Email not available</div>
                                        )
                                    )}
                                </div>
                                <span className="inline-flex items-center text-[11px] font-bold px-2.5 py-1 rounded-full flex-shrink-0" style={{ background: '#fff', border: '1px solid #E0E5FC', color: '#3F57E0' }}>{tierLabel}</span>
                            </div>

                            {/* Subject — emails only; LinkedIn messages/notes have no heading */}
                            {!isLinkedInMode && (
                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-1.5">
                                    <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#94A3B8]">{subjectLabel}</label>
                                    <span className="text-[11px] text-[#94A3B8]">{editableSubject.length} chars</span>
                                </div>
                                <div className="flex items-center gap-2.5 bg-white" style={{ padding: '9px 12px', borderRadius: 10, border: '1px solid #E6E8EE' }}>
                                    <input
                                        disabled={isNoteMode}
                                        className="flex-1 bg-transparent outline-none text-[13.5px] font-semibold text-[#0F172A]"
                                        value={editableSubject}
                                        onChange={(e) => setEditableSubject(e.target.value)}
                                    />
                                    <Pencil size={13} className="text-[#94A3B8] flex-shrink-0" />
                                </div>
                            </div>
                            )}

                            {/* Body */}
                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#94A3B8] inline-flex items-center gap-1.5">{bodyLabel} <Pencil size={11} className="text-[#94A3B8]" /></label>
                                    <span className="text-[11px] text-[#94A3B8] inline-flex items-center gap-1"><Clock size={11} /> ~{readSec} sec read</span>
                                </div>
                                <div className="rounded-xl border border-[#E6E8EE] bg-[#FAFBFD] focus-within:border-[#3F57E0] focus-within:bg-white transition-colors">
                                    <textarea
                                        maxLength={isNoteMode ? MAX_NOTE_LENGTH : undefined}
                                        className="w-full bg-transparent outline-none text-[13px] text-[#334155] leading-relaxed resize-y"
                                        style={{ padding: '14px 16px', minHeight: 150 }}
                                        value={editableBody}
                                        onChange={(e) => {
                                            const nextValue = e.target.value;
                                            setEditableBody(isNoteMode ? clampLinkedInNote(nextValue) : nextValue);
                                        }}
                                    />
                                </div>
                                {isNoteMode && (
                                    <p className={`mt-2 text-xs font-medium text-right ${currentLength >= MAX_NOTE_LENGTH ? 'text-rose-600' : 'text-slate-500'}`}>
                                        {currentLength}/{MAX_NOTE_LENGTH} characters
                                    </p>
                                )}
                            </div>

                            {/* Refine */}
                            <div className="mt-4" style={{ padding: '14px 16px', borderRadius: 14, background: '#F5F3FF', border: '1px solid #E9D5FF' }}>
                                <div className="flex items-center gap-1.5 mb-2.5">
                                    <Sparkles size={13} style={{ color: '#7C3AED' }} />
                                    <span className="text-[12.5px] font-bold" style={{ color: '#6D28D9' }}>Refine with a prompt</span>
                                    <span className="text-[11px] text-[#94A3B8]">· optional</span>
                                </div>
                                <div className="flex gap-2">
                                    <div className="flex-1 flex items-center bg-white" style={{ padding: '9px 12px', borderRadius: 10, border: '1px solid #E9D5FF' }}>
                                        <input
                                            placeholder="e.g. make this shorter and more formal"
                                            className="flex-1 border-0 outline-none text-[13px] text-[#0F172A] bg-transparent"
                                            value={promptText}
                                            onChange={(e) => setPromptText(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter' && promptText.trim()) generateEmail(promptText); }}
                                        />
                                    </div>
                                    <button onClick={() => generateEmail(promptText)} className="inline-flex items-center gap-1.5 text-white text-[13px] font-semibold rounded-[10px]" style={{ background: '#7C3AED', padding: '9px 16px' }}>
                                        <Sparkles size={13} /> Generate
                                    </button>
                                    <button onClick={() => { setPromptText(''); generateEmail(); }} className="inline-flex items-center text-[13px] font-semibold text-[#334155] bg-white rounded-[10px]" style={{ padding: '9px 14px', border: '1px solid #E9D5FF' }}>
                                        Another
                                    </button>
                                </div>
                                <div className="flex gap-1.5 mt-2.5 flex-wrap">
                                    {refineChips.map((s) => (
                                        <button
                                            key={s}
                                            onClick={() => generateEmail(s)}
                                            className="text-[11px] font-semibold rounded-full bg-white"
                                            style={{ padding: '4px 10px', border: '1px solid #E9D5FF', color: '#6D28D9' }}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-[#EFF1F5] bg-white mt-auto">
                            {!isLinkedInMode ? (
                                <div className="flex flex-col gap-2.5">
                                    <div className="flex items-center gap-3">
                                        <button onClick={copyToClipboard} className="flex items-center justify-center gap-2 text-[13px] font-semibold text-[#334155] bg-white rounded-xl border border-[#E6E8EE] hover:border-[#D6DAE3] transition-colors" style={{ flex: 1, padding: 12 }}>
                                            {copied ? <><Check size={15} /> Copied!</> : <><Copy size={15} /> Copy Email Content</>}
                                        </button>
                                        {isGoogleConnected ? (
                                            <button onClick={handleSendViaGmail} disabled={isSendingEmail || !contact?.email} className="flex items-center justify-center gap-2 text-[13px] font-semibold text-white bg-[#3F57E0] hover:bg-[#3347C8] rounded-xl transition-colors disabled:opacity-60" style={{ flex: 1.4, padding: 12, boxShadow: '0 8px 20px rgba(63,87,224,.3)' }}>
                                                {isSendingEmail ? <><Loader2 size={15} className="animate-spin" /> Sending…</> : <><Send size={15} /> Send Email</>}
                                            </button>
                                        ) : (
                                            <button onClick={handleConnectGoogle} className="flex items-center justify-center gap-2 text-[13px] font-semibold text-white bg-[#3F57E0] hover:bg-[#3347C8] rounded-xl transition-colors" style={{ flex: 1.4, padding: 12, boxShadow: '0 8px 20px rgba(63,87,224,.3)' }}>
                                                <ExternalLink size={15} /> Connect Google to send
                                            </button>
                                        )}
                                    </div>
                                    {!isGoogleConnected && (
                                        <p className="text-[11.5px] text-[#94A3B8] self-center text-center">
                                            Sending uses your Gmail — connect once to send as yourself. You can copy the draft meanwhile.
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <div className="flex flex-col gap-3">
                                    <button onClick={copyToClipboard} className="w-full flex items-center justify-center gap-2 text-[13px] font-semibold text-[#334155] bg-white rounded-xl border border-[#E6E8EE] hover:border-[#D6DAE3] transition-colors" style={{ padding: 12 }}>
                                        {copied ? <><Check size={15} /> Copied!</> : <><Copy size={15} /> Copy {isNoteMode ? 'Note' : 'Message'}</>}
                                    </button>
                                    {!isLinkedInConnected?.accountId ? (
                                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                                            <p className="text-sm text-amber-800 font-bold mb-1">LinkedIn Not Connected</p>
                                            <p className="text-xs text-amber-700 mb-3">Connect your LinkedIn in Networking to send automated messages.</p>
                                            <button onClick={() => window.location.href = '/networking'} className="inline-flex items-center gap-2 text-xs bg-amber-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-amber-700 transition-all shadow-sm">
                                                <Linkedin size={14} className="fill-white" /> Connect Now
                                            </button>
                                        </div>
                                    ) : needsConnectionForMessage ? (
                                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                                            <p className="text-sm text-slate-800 font-bold mb-1">Connection Check</p>
                                            <p className="text-xs text-slate-600 mb-4">You are not connected with {contact?.name} yet. Connect first, or continue using LinkedIn InMail.</p>
                                            <div className="grid grid-cols-1 gap-2">
                                                <button onClick={() => onClose()} className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-all shadow-md">
                                                    <UserPlus size={18} /> Send Connection Request First
                                                </button>
                                                <button onClick={() => setAllowInMail(true)} className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-white text-slate-700 rounded-lg font-bold border border-slate-200 hover:bg-slate-50 transition-all">
                                                    <Linkedin size={18} /> Continue With InMail
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <button onClick={handleSendToLinkedIn} disabled={isSendingToLi} className="w-full flex items-center justify-center gap-2.5 px-6 py-3.5 bg-emerald-600 text-white rounded-xl font-bold text-[14px] hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-100 disabled:bg-emerald-300 disabled:cursor-wait">
                                                {isSendingToLi ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : <><Send size={18} /> {isNoteMode ? 'Send Invitation Note' : (outreachMode === 'message' && inviteStatus !== 'accepted' ? 'Send via InMail' : 'Send via LinkedIn')}</>}
                                            </button>
                                            {!isSendingToLi && (
                                                <button onClick={() => { navigator.clipboard.writeText(editableBody); toast.success('Text copied to clipboard!'); window.open(contact?.linkedin_url, '_blank'); onClose(); }} className="w-full flex items-center justify-center gap-2 py-1 text-slate-500 hover:text-slate-700 text-xs font-semibold transition-all">
                                                    <ExternalLink size={14} /> Automation failed? Copy &amp; Open Profile Manually
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                ) : null}
            </div>
        </div>,
        document.body
    );
};

export default EmailGenerationModal;
